import { IWorkbookDataFreshnessDescription } from '~src/dataAcceleration/models/IDataFreshnessInfo';
import { FlowParameter, FlowParameterRun } from '~src/flow/types/FlowParameters';
import { TableauDataType } from '~src/publishedConnectionTable/types/TableauDataType';

import { ExtractEncryptionState } from '../../datasource/models/IDataSourceInfo';
import { ContentDraftStatus, FlowDraft } from '../../drafts/serverApi/types/ContentDraft';
import { IUser } from '../../userManagement/users/serverApi/types/IUser';

// This file holds interfaces that are extended by other ServerApi interfaces
// The interfaces in this file should not be stand-alone interfaces

export interface ICertifiableItem {
  isCertified: boolean;
  certificationNote?: string;
  certifier?: IUser;
}

export interface ICatalogCertifiableItem {
  isCertified: boolean;
  certificationNote?: string;
  certifier?: ICatalogUserItem;
}

export interface ICreatedItem {
  createdAt: string; // datetime(ISO 8601 UTC): YYYY-MM-DDThh:mm:ssZ
}

export interface DataSourceItemOptional {
  readonly datasourceId?: string;
}

export interface IDescriptionItem {
  description: string;
}

export interface IFavoriteDetailItem {
  favorite: boolean;
}

export interface IBroadcastDetailItem {
  broadcast: boolean;
}

export interface FlowItem {
  readonly flowId: string;
  readonly flowLuid?: string;
}

export interface FlowItemOptional {
  readonly flowId?: string;
}

export interface FlowItemWithGraphImage {
  readonly flowGraphImageUrl: string;
}

export interface FlowItemWithFileType {
  readonly fileType: string;
}

export interface FlowItemWithDocumentVersion {
  readonly documentVersion: string;
}

export interface FlowItemWithThumbnail {
  readonly thumbnailUrl: string;
}

export interface FlowDraftItem {
  flowDraft: FlowDraft;
}

export interface FlowParameterItem {
  relevantParameters?: FlowParameter[];
}

export interface FlowParameterRunItem {
  relevantParameterRuns?: FlowParameterRun[];
}

export interface DraftStatusItem {
  draftStatus: ContentDraftStatus;
}

export interface DraftLuidItem {
  draftLuid: string;
}

export interface DraftIdItem {
  draftId: string;
}

export interface IIdItem {
  id: string;
}

export interface ILuidItem {
  luid?: string;
}

export interface INamedItem extends IIdItem, IContentsItem, ILuidItem {
  name?: string;
}

export interface IContentsItem {
  contents?: any;
}

export interface IOwnedItem {
  ownerId: string;
}

export interface IOwnedDetailItem {
  owner: IUser;
}

export interface IModifiedByUserDetailItem {
  modifiedByUser: IUser;
}

export interface IModifiedByItem {
  modifiedByUserId: string;
}

export interface IProjectItem {
  projectId: string;
}

export interface IProjectDetailItem {
  project: INamedItem;
}

export interface IWorkbookDataFreshnessItem {
  dataFreshnessViewModel?: IWorkbookDataFreshnessDescription;
}

export interface ITaggedItem {
  tags: string[];
}

export interface IThumbnailItem {
  thumbnailUrl: string;
}

export interface IDownloadItem {
  downloadUrl: string;
}

export interface IUpdatedItem {
  updatedAt?: string; // datetime(ISO 8601 UTC): YYYY-MM-DDThh:mm:ssZ
}

export interface IEditableItem {
  // This property will be defined if the workbook is editable, example: workbooks without stories
  editUrl?: string;
}

export interface IItemStatistics<T extends _.Dictionary<number>> {
  usageInfo: T;
}

export interface IStatsTimeSeries {
  hitsTimeSeries?: number[];
}

export interface IEncryptableExtractItem {
  hasExtracts: boolean;
  extractEncryptionState: ExtractEncryptionState;
  hasEncryptableExtracts: boolean;
}

export interface IExtractItem extends IEncryptableExtractItem {
  lastRefreshedAt?: string; // datetime(ISO 8601 UTC): YYYY-MM-DDThh:mm:ssZ, doesn't exist if it's a live connection
  hasExtracts: boolean;
  hasAlert: boolean;
  hasIncrementalExtract: boolean;
  extractCreationPending: boolean;
  extractEncryptionState: ExtractEncryptionState;
  hasEncryptableExtracts: boolean;
}

export interface IScheduledItem {
  scheduleId: string;
}

export interface ISiteItem {
  siteId: string;
}

export interface IChildItem {
  parentProjectId: string;
}

export interface IPathItem {
  path: string;
}

export interface ITypedItem extends INamedItem {
  type: string; // enforce that it is present, but values are defined at higher levels
  count?: number;
}

export interface ISuspendedItem {
  suspended: boolean;
}

// Key Objects
//TODO Figure out a way to remove the dependency on these Key variables and then remove it

export let NamedItemKeys = {
  id: 'id',
  name: 'name'
};


export interface IDataQualityWarningItem {
  hasSevereDataQualityWarning?: boolean;
  hasActiveDataQualityWarning?: boolean;
}

export interface IDataQualityIndicator extends IIdItem {
  isActive: boolean;
  isSevere: boolean;
  type: string;
  message: string;
  updatedAt: string;
  owner: IUser;
}

export interface IDownstreamItem {
  downstreamWorkbookCount: number;
  downstreamDataSourceCount: number;
}

export interface IDownstreamConnection {
  totalCount: number;
  typeName: string;
}

export interface ICatalogContactItem {
  contact: ICatalogUserItem;
}

export interface ICatalogUserItem {
  id: string;
  displayName: string;
  username: string;
  domainName?: string;
  readOnly?: boolean;
  hasServerDisplay?: boolean;
}

export interface ITableDatabaseItem {
  connectionType: string;
  dbType: string;
  hostName?: string;
  isEmbedded: boolean;
  isGroupedParent: boolean;
  name: string;
  tags: string[];
}

export interface ITableColumnItem {
  columnName: string;
}

export interface IPublishedConnectionItem {
  publishedConnectionId: string;
}

export interface IPublishedConnectionDetailItem {
  publishedConnectionId: string;
}

export interface IDataPolicyItem {
  hasDataPolicy: boolean;
}

export interface ITableauDataTypeItem {
  dataType: TableauDataType;
}

export interface IPublishedConnectionExtractItem {
  hasExtract: boolean;
  extractsRefreshedAt: string;
}

export interface ILuidAndNameItem {
  luid: string;
  name: string;
}

export interface IPublishedConnectionWithTablesItem extends INamedItem {
  tables: ILuidAndNameItem[];
}
